import { Property, PropertyStatus, PropertyType, UnitStatus } from '../../../types';
import { DISTRICT_MAP } from './propertyFormConstants';

export interface PropertyFormData {
  title: string;
  description: string;
  latitude: number;
  longitude: number;
  address: string;
  city: string;
  country: string;
  postalCode: string;
  governorate: string;
  district: string;
  town: string;
  propertyType: PropertyType;
  status: PropertyStatus;
  price: string;
  size: string;
  bedrooms: string;
  bathrooms: string;
  propertyAge: string;
  paymentMethod: string;
  downPayment: string;
  monthlyPayment: string;
  transactionType: string;
  agentId: string;
  features: string[];
  ownerName: string;
  ownerPhone: string;
  googleMapsLink: string;
  floor: string;
  totalFloors: string;
  elevator: string;
  maidRoom: string;
  furnished: string;
  parking: string;
  waterTanks: string;
  warehouseType: string;
  warehouseFloat: string;
  warehouseAccessType: string;
  vehicleType: string;
  kitchen: string;
  rooms: string;
  landType: string;
  zone: string;
  projectId: string;
  floorNumber: string;
  unitNumber: string;
  unitStatus: string;
  notes: string;
  landSize: string;
}

export const initialPropertyFormData: PropertyFormData = {
  title: '',
  description: '',
  latitude: 33.8547,
  longitude: 35.8623,
  address: '',
  city: '',
  country: 'Lebanon',
  postalCode: '',
  governorate: '',
  district: '',
  town: '',
  propertyType: PropertyType.APARTMENT,
  status: PropertyStatus.PENDING,
  price: '',
  size: '',
  bedrooms: '',
  bathrooms: '',
  propertyAge: '',
  paymentMethod: '',
  downPayment: '',
  monthlyPayment: '',
  transactionType: '',
  agentId: '',
  features: [],
  ownerName: '',
  ownerPhone: '',
  googleMapsLink: '',
  floor: '',
  totalFloors: '',
  elevator: '',
  maidRoom: '',
  furnished: '',
  parking: '',
  waterTanks: '',
  warehouseType: '',
  warehouseFloat: '',
  warehouseAccessType: '',
  vehicleType: '',
  kitchen: '',
  rooms: '',
  landType: '',
  zone: '',
  projectId: '',
  floorNumber: '',
  unitNumber: '',
  unitStatus: '',
  notes: '',
  landSize: '',
};

export const getDistrictOptions = (governorate: string): string[] =>
  governorate ? DISTRICT_MAP[governorate] || [] : [];

type ExtendedProperty = Property & {
  downPayment?: number;
  monthlyPayment?: number;
  warehouseType?: string;
  warehouseFloat?: number;
  warehouseAccessType?: string;
  vehicleType?: string;
  kitchen?: string;
  rooms?: number;
  landType?: string;
  zone?: string;
  notes?: string;
  landSize?: number;
};

export const createPropertyFormData = (property?: Property | null): PropertyFormData => {
  if (!property) return { ...initialPropertyFormData };
  const p = property as ExtendedProperty;
  return {
    title: p.title,
    description: p.description || '',
    latitude: p.latitude,
    longitude: p.longitude,
    address: p.address,
    city: p.city,
    country: p.country,
    postalCode: p.postalCode || '',
    governorate: p.governorate || '',
    district: p.district || '',
    town: p.town || '',
    propertyType: p.propertyType,
    status: p.status,
    price: p.price.toString(),
    size: p.size?.toString() || '',
    bedrooms: p.bedrooms?.toString() || '',
    bathrooms: p.bathrooms?.toString() || '',
    propertyAge: p.propertyAge?.toString() || '',
    paymentMethod: Array.isArray(p.paymentMethod) ? (p.paymentMethod[0] || '') : (p.paymentMethod || ''),
    downPayment: p.downPayment?.toString() || '',
    monthlyPayment: p.monthlyPayment?.toString() || '',
    transactionType: p.transactionType || '',
    agentId: p.agentId || '',
    features: p.features || [],
    ownerName: p.ownerName || '',
    ownerPhone: p.ownerPhone || '',
    googleMapsLink: p.googleMapsLink || '',
    floor: p.floor?.toString() || '',
    totalFloors: p.totalFloors?.toString() || '',
    elevator: p.elevator || '',
    maidRoom: p.maidRoom || '',
    furnished: p.furnished || '',
    parking: p.parking != null ? p.parking.toString() : '',
    waterTanks: p.waterTanks != null ? p.waterTanks.toString() : '',
    warehouseType: p.warehouseType || '',
    warehouseFloat: p.warehouseFloat?.toString() || '',
    warehouseAccessType: p.warehouseAccessType || '',
    vehicleType: p.vehicleType || '',
    kitchen: p.kitchen || '',
    rooms: p.rooms?.toString() || '',
    landType: p.landType || '',
    zone: p.zone || '',
    projectId: p.projectId || '',
    floorNumber: p.floorNumber?.toString() || '',
    unitNumber: p.unitNumber || '',
    unitStatus: p.unitStatus || '',
    notes: p.notes || '',
    landSize: p.landSize?.toString() || '',
  };
};

export const validatePropertyForm = (formData: PropertyFormData): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!formData.title.trim()) errors.title = 'Property title is required';
  if (!formData.address.trim()) errors.address = 'Address is required';
  if (!formData.city.trim()) errors.city = 'City is required';
  if (!formData.governorate) errors.governorate = 'Governorate is required';
  if (!formData.ownerName.trim()) errors.ownerName = 'Owner name is required';
  if (!formData.ownerPhone.trim()) errors.ownerPhone = 'Owner phone is required';
  if (!formData.price.trim()) {
    errors.price = 'Price is required';
  } else if (parseFloat(formData.price) <= 0) {
    errors.price = 'Price must be greater than 0';
  }
  if (formData.latitude < -90 || formData.latitude > 90 || Number.isNaN(formData.latitude))
    errors.latitude = 'Latitude must be between -90 and 90';
  if (formData.longitude < -180 || formData.longitude > 180 || Number.isNaN(formData.longitude))
    errors.longitude = 'Longitude must be between -180 and 180';
  if (formData.size && parseFloat(formData.size) <= 0) errors.size = 'Size must be greater than 0';
  if (formData.landSize && parseFloat(formData.landSize) <= 0) errors.landSize = 'Land size must be greater than 0';
  const needsLandAndInteriorSize = [PropertyType.VILLA, PropertyType.TOWNHOUSE, PropertyType.BUILDING, PropertyType.UNIT].includes(formData.propertyType);
  if (needsLandAndInteriorSize) {
    if (!formData.size) errors.size = 'Interior size is required for this property type';
    if (!formData.landSize) errors.landSize = 'Land size is required for this property type';
  }
  if (formData.bedrooms && parseInt(formData.bedrooms, 10) < 0) errors.bedrooms = 'Bedrooms cannot be negative';
  if (formData.bathrooms && parseFloat(formData.bathrooms) < 0) errors.bathrooms = 'Bathrooms cannot be negative';
  if (formData.totalFloors && parseInt(formData.totalFloors, 10) <= 0) errors.totalFloors = 'Total floors must be greater than 0';
  if (formData.floorNumber && parseInt(formData.floorNumber, 10) < -1) errors.floorNumber = 'Floor number must be -1 or higher';
  if (formData.floorNumber && formData.totalFloors && parseInt(formData.floorNumber, 10) > parseInt(formData.totalFloors, 10))
    errors.floorNumber = 'Floor number cannot exceed total floors';
  if (formData.floor && formData.totalFloors && parseInt(formData.floor, 10) > parseInt(formData.totalFloors, 10))
    errors.floor = 'Floor cannot exceed total floors';
  if (formData.propertyAge && parseInt(formData.propertyAge, 10) < 0) errors.propertyAge = 'Property age cannot be negative';
  return errors;
};

export const createPropertyPayload = (formData: PropertyFormData, uploadedImages: string[]) => ({
  title: formData.title,
  description: formData.description,
  latitude: formData.latitude,
  longitude: formData.longitude,
  address: formData.address,
  city: formData.city,
  country: formData.country,
  postalCode: formData.postalCode || undefined,
  governorate: formData.governorate || undefined,
  district: formData.district || undefined,
  town: formData.town || undefined,
  propertyType: formData.propertyType,
  status: formData.status,
  price: parseFloat(formData.price),
  size: formData.size ? parseFloat(formData.size) : undefined,
  bedrooms: formData.bedrooms ? parseInt(formData.bedrooms, 10) : undefined,
  bathrooms: formData.bathrooms ? parseFloat(formData.bathrooms) : undefined,
  propertyAge: formData.propertyAge ? parseInt(formData.propertyAge, 10) : undefined,
  paymentMethod: formData.paymentMethod || undefined,
  downPayment: formData.downPayment ? parseFloat(formData.downPayment) : undefined,
  monthlyPayment: formData.monthlyPayment ? parseFloat(formData.monthlyPayment) : undefined,
  transactionType: formData.transactionType || undefined,
  agentId: formData.agentId || undefined,
  features: formData.features,
  images: uploadedImages,
  ownerName: formData.ownerName || undefined,
  ownerPhone: formData.ownerPhone || undefined,
  googleMapsLink: formData.googleMapsLink || undefined,
  floor: formData.floor ? parseInt(formData.floor, 10) : undefined,
  totalFloors: formData.totalFloors ? parseInt(formData.totalFloors, 10) : undefined,
  elevator: formData.elevator || undefined,
  maidRoom: formData.maidRoom || undefined,
  furnished: formData.furnished || undefined,
  parking: formData.parking !== '' ? parseInt(formData.parking, 10) : undefined,
  waterTanks: formData.waterTanks !== '' ? parseFloat(formData.waterTanks) : undefined,
  warehouseType: formData.warehouseType || undefined,
  warehouseFloat: formData.warehouseFloat || undefined,
  warehouseAccessType: formData.warehouseAccessType || undefined,
  vehicleType: formData.vehicleType || undefined,
  kitchen: formData.kitchen || undefined,
  rooms: formData.rooms || undefined,
  landType: formData.landType || undefined,
  zone: formData.zone || undefined,
  projectId: formData.projectId || undefined,
  floorNumber: formData.floorNumber ? parseInt(formData.floorNumber, 10) : undefined,
  unitNumber: formData.unitNumber || undefined,
  unitStatus: (formData.unitStatus as UnitStatus) || undefined,
  notes: formData.notes || undefined,
  landSize: formData.landSize ? parseFloat(formData.landSize) : undefined,
});
